import React, { useEffect, useRef, useState, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
} from "react-router-dom";
import { Provider } from "react-redux";
import { HelmetProvider } from "react-helmet-async";
import { store } from "./store";
import { useAppDispatch, useAppSelector } from "./store";
import { getCurrentUser, getPermissionContext } from "./store/slices/authSlice";
import { setTheme } from "./store/slices/uiSlice";

// Layouts (keep eagerly loaded as they're always needed)
import AuthLayout from "./components/layouts/AuthLayout";
import DashboardLayout from "./components/layouts/DashboardLayout";
import PublicLayout from "./components/layouts/PublicLayout";
import TenantLayout from "./components/layouts/TenantLayout";

// Components
import LoadingSpinner from "./components/ui/LoadingSpinner";
import PageLoader from "./components/ui/PageLoader";
import Toast from "./components/ui/Toast";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import PublicRoute from "./components/auth/PublicRoute";
import RoleBasedRedirect from "./components/auth/RoleBasedRedirect";
import ScrollToTop from "./components/common/ScrollToTop";
// import { PermissionBasedRoute, AdminRoute, TenantRoute, FeatureRoute } from './components/auth/PermissionBasedRoute';
import { PermissionProvider } from "./contexts/PermissionContext";
import { FeatureGate } from "./components/common/PermissionGate";
import { FEATURE_PERMISSIONS } from "./constants/featurePermissions";
import { SuperAdminRoute } from "./components/auth/PermissionBasedRoute";

import "./App.css";
import { initializeGlobalImageErrorHandler } from "./utils/imageErrorHandler";
import { fetchCompanies } from "./store/slices/companiesSlice";
import GTMScript from "./components/GTMScript";
import RBChatbot from "./components/RBChatbot";
import { BRAND_NAME } from "./config/brand";

// Lazy-loaded Pages - Public Pages (grouped in public bundle)
const HomePage = React.lazy(
  () => import(/* webpackChunkName: "public-pages" */ "./pages/public/HomePage")
);
const PropertiesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-pages" */ "./pages/public/PropertiesPage"
    )
);
const SinglePropertyPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-pages" */ "./pages/public/SinglePropertyPage"
    )
);
const FeaturesPage = React.lazy(
  () =>
    import(/* webpackChunkName: "public-pages" */ "./pages/public/FeaturesPage")
);
const ContactPage = React.lazy(
  () =>
    import(/* webpackChunkName: "public-pages" */ "./pages/public/ContactPage")
);
const PricingPage = React.lazy(
  () =>
    import(/* webpackChunkName: "public-pages" */ "./pages/public/PricingPage")
);
const PrivacyPolicyPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-pages" */ "./pages/public/PrivacyPolicyPage"
    )
);
const TermsConditionsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-pages" */ "./pages/public/TermsConditionsPage"
    )
);

// Lazy-loaded Auth Pages (grouped in auth bundle)
const LoginPage = React.lazy(
  () => import(/* webpackChunkName: "auth-pages" */ "./pages/auth/LoginPage")
);
const RegisterPage = React.lazy(
  () => import(/* webpackChunkName: "auth-pages" */ "./pages/auth/RegisterPage")
);
const ForgotPasswordPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "auth-pages" */ "./pages/auth/ForgotPasswordPage"
    )
);
const ResetPasswordPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "auth-pages" */ "./pages/auth/ResetPasswordPage"
    )
);
const VerifyEmailPage = React.lazy(
  () =>
    import(/* webpackChunkName: "auth-pages" */ "./pages/auth/VerifyEmailPage")
);
const VerifyPhonePage = React.lazy(
  () =>
    import(/* webpackChunkName: "auth-pages" */ "./pages/auth/VerifyPhonePage")
);
const VerifyEmailPendingPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "auth-pages" */ "./pages/auth/VerifyEmailPendingPage"
    )
);
const InvitationAcceptancePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "auth-pages" */ "./pages/InvitationAcceptancePage"
    )
);

// Lazy-loaded Dashboard Pages (split by feature for optimal loading)
const DashboardHomePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-core" */ "./pages/dashboard/DashboardHomePage"
    )
);
const ProfilePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-core" */ "./pages/dashboard/ProfilePage"
    )
);

// Property Management (separate bundle - heavy component)
const PropertyManagementPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "property-management" */ "./pages/dashboard/PropertyManagementPage"
    )
);

// Tenant Management
const TenantManagementPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tenant-management" */ "./pages/dashboard/TenantManagementPage"
    )
);
const TenantsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tenant-management" */ "./pages/dashboard/TenantsPage"
    )
);

// Company Management (separate bundle - heavy component)
const CompanyManagementPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "company-management" */ "./pages/dashboard/CompanyManagementPage"
    )
);
const CompanyUsersPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "company-management" */ "./pages/dashboard/CompanyUsersPage"
    )
);

// Booking & Maintenance
const BookingsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "booking-maintenance" */ "./pages/dashboard/BookingsPage"
    )
);
const MaintenancePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "booking-maintenance" */ "./pages/dashboard/MaintenancePage"
    )
);

// Financial
const PaymentsPage = React.lazy(
  () =>
    import(/* webpackChunkName: "financial" */ "./pages/dashboard/PaymentsPage")
);
const InvoicesPage = React.lazy(
  () =>
    import(/* webpackChunkName: "financial" */ "./pages/dashboard/InvoicesPage")
);
const ExpensesPage = React.lazy(
  () =>
    import(/* webpackChunkName: "financial" */ "./pages/dashboard/ExpensesPage")
);

// Analytics & Reports
const AnalyticsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "analytics" */ "./pages/dashboard/AnalyticsPage"
    )
);

// Communication
const BroadcastPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "communication" */ "./pages/dashboard/BroadcastPage"
    )
);

// Admin & System
const NotificationsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "admin-system" */ "./pages/dashboard/NotificationsPage"
    )
);
const AdminManagementPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "admin-system" */ "./pages/dashboard/AdminManagementPage"
    )
);
const PlansPage = React.lazy(
  () =>
    import(/* webpackChunkName: "admin-system" */ "./pages/dashboard/PlansPage")
);
const TestNotificationsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "admin-system" */ "./pages/dashboard/TestNotificationsPage"
    )
);
const NotificationLogsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "admin-system" */ "./pages/dashboard/NotificationLogsPage"
    )
);
const AuthLogsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "admin-system" */ "./pages/dashboard/AuthLogsPage"
    )
);
const ChargebeeLogsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "admin-system" */ "./pages/dashboard/ChargebeeLogsPage"
    )
);
const LeadsPage = React.lazy(
  () =>
    import(/* webpackChunkName: "admin-system" */ "./pages/dashboard/LeadsPage")
);
const BulkOnboardingPage = React.lazy(
  () =>
    import(/* webpackChunkName: "admin-system" */ "./pages/dashboard/BulkOnboardingPage")
);

// Catalogue Config (website customization)
const CatalogueConfigPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "admin-system" */ "./pages/dashboard/CatalogueConfigPage"
    )
);

// Virtual Tours & Maps (separate bundle - potentially heavy with 3D/map assets)
const VirtualToursPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "virtual-tours" */ "./pages/dashboard/VirtualToursPage"
    )
);
const MapDemoPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "virtual-tours" */ "./pages/dashboard/MapDemoPage"
    )
);

// Lazy-loaded Tenant Pages (grouped in tenant bundle)
const TenantDashboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tenant-pages" */ "./pages/tenant/TenantDashboard"
    )
);
const TenantUnitsList = React.lazy(
  () =>
    import(/* webpackChunkName: "tenant-pages" */ "./pages/tenant/UnitsList")
);
const TenantUnitDetail = React.lazy(
  () =>
    import(/* webpackChunkName: "tenant-pages" */ "./pages/tenant/UnitDetail")
);
const TenantMaintenancePage = React.lazy(
  () =>
    import(/* webpackChunkName: "tenant-pages" */ "./pages/tenant/Maintenance")
);
const TenantNotificationsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tenant-pages" */ "./pages/tenant/Notifications"
    )
);
const TenantInvoicesPage = React.lazy(
  () => import(/* webpackChunkName: "tenant-pages" */ "./pages/tenant/Invoices")
);
const TenantDeleteAccount = React.lazy(
  () => import(/* webpackChunkName: "tenant-pages" */ "./pages/tenant/DeleteAccount")
);
const AdminDeleteAccount = React.lazy(
  () => import(/* webpackChunkName: "dashboard-pages" */ "./pages/dashboard/AdminDeleteAccount")
);

// Lazy-loaded Public Components (grouped with their respective features)
const PublicMaintenanceRequestForm = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-forms" */ "./components/public/PublicMaintenanceRequestForm"
    )
);
const PublicTenantPortal = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-portals" */ "./components/public/PublicTenantPortal"
    )
);
const PublicAssigneeLayout = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-portals" */ "./components/public/PublicAssigneeLayout"
    )
);
const PublicAssigneePortal = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-portals" */ "./components/public/PublicAssigneePortal"
    )
);
const AssigneePublicPassbook = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-portals" */ "./components/public/AssigneePublicPassbook"
    )
);
const PublicPaymentPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-payments" */ "./components/public/PublicPaymentPage"
    )
);
const PublicWebInvoicePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-payments" */ "./components/public/PublicWebInvoicePage"
    )
);
const BroadcastViewerPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-broadcast" */ "./pages/public/BroadcastViewerPage"
    )
);

// Lazy-loaded Tenant Components
const TenantProfileView = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tenant-profile" */ "./components/tenant/TenantProfileView"
    )
);

function AppContent() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, token } = useAppSelector(
    (state) => state.auth
  );
  const { theme } = useAppSelector((state) => state.ui);
  const hasCalledGetCurrentUser = useRef(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  useEffect(() => {
    // Initialize global image error handler
    initializeGlobalImageErrorHandler();

    // Initialize theme
    // const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    // if (savedTheme) {
    //   dispatch(setTheme(savedTheme));
    // } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    //   dispatch(setTheme('dark'));
    // }

    // if (theme === 'dark') {
    //   document.documentElement.classList.add('dark');
    // } else {
    document.documentElement.classList.remove("dark");
    // }
  }, [dispatch, theme]);

  useEffect(() => {
    // Reset the ref when user is cleared (logout)
    if (!token || !isAuthenticated) {
      hasCalledGetCurrentUser.current = false;
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (token && isAuthenticated && !hasCalledGetCurrentUser.current) {
      hasCalledGetCurrentUser.current = true;
      setIsInitialLoading(true);

      const doBootstrap = async () => {
        try {
          const fetchedUser = await dispatch(getCurrentUser()).unwrap();
          const role = fetchedUser?.role as string | undefined;

          const isTenant = role === "tenant";
          if (!isTenant) {
            // Admin/company-user bootstrap: companies + permission context
            await dispatch(fetchCompanies()).unwrap();
            await dispatch(getPermissionContext());
          } else {
            // Tenant: skip admin bootstrap entirely
          }
        } catch (error) {
          console.error("Error loading initial data:", error);
        } finally {
          setIsInitialLoading(false);
        }
      };

      void doBootstrap();
    }
  }, [token, isAuthenticated, dispatch]); // Gate bootstrap by role; tenants skip admin calls

  // Only show full page loading during initial app load (getCurrentUser)
  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        {/* <InstallPWA /> */}
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicLayout />}>
            <Route
              index
              element={<Navigate to="/auth/login" replace />}
            />

          </Route>

          {/* Public Maintenance Request Form */}
          <Route
            path="/create-ticket/:formId"
            element={
              <Suspense
                fallback={<PageLoader message="Loading form..." fullScreen />}
              >
                <PublicMaintenanceRequestForm />
              </Suspense>
            }
          />

          {/* Public Tenant Portal */}
          <Route
            path="/tenant-portal/:tenantcode"
            element={
              <Suspense
                fallback={
                  <PageLoader message="Loading tenant portal..." fullScreen />
                }
              >
                <PublicTenantPortal />
              </Suspense>
            }
          />

          {/* Public Assignee Portal — shared layout persists navbar across tab switches */}
          <Route
            path="/assignee-portal/:assigneeCode"
            element={
              <Suspense fallback={<PageLoader message="Loading assignee portal..." fullScreen />}>
                <PublicAssigneeLayout />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={null}>
                  <PublicAssigneePortal />
                </Suspense>
              }
            />
            <Route
              path="passbook"
              element={
                <Suspense fallback={null}>
                  <AssigneePublicPassbook />
                </Suspense>
              }
            />
          </Route>

          {/* Public Payment Collection Page */}
          <Route
            path="/collect-payment/:linkId"
            element={
              <Suspense
                fallback={
                  <PageLoader message="Loading payment page..." fullScreen />
                }
              >
                <PublicPaymentPage />
              </Suspense>
            }
          />

          {/* Public Web Invoice Page */}
          <Route
            path="/web-invoice/:invoiceCustomId"
            element={
              <Suspense
                fallback={
                  <PageLoader message="Loading invoice..." fullScreen />
                }
              >
                <PublicWebInvoicePage />
              </Suspense>
            }
          />

          {/* Public Broadcast Viewer Page */}
          <Route
            path="/broadcast/:token"
            element={
              <Suspense
                fallback={
                  <PageLoader message="Loading message..." fullScreen />
                }
              >
                <BroadcastViewerPage />
              </Suspense>
            }
          />

          {/* Auth Routes */}
          <Route path="/auth" element={<AuthLayout />}>
            <Route
              path="login"
              element={
                <PublicRoute>
                  <Suspense
                    fallback={<PageLoader message="Loading login..." />}
                  >
                    <LoginPage />
                  </Suspense>
                </PublicRoute>
              }
            />
            <Route
              path="register"
              element={
                <PublicRoute>
                  <Suspense
                    fallback={<PageLoader message="Loading registration..." />}
                  >
                    <RegisterPage />
                  </Suspense>
                </PublicRoute>
              }
            />
            <Route
              path="forgot-password"
              element={
                <PublicRoute>
                  <Suspense
                    fallback={
                      <PageLoader message="Loading password reset..." />
                    }
                  >
                    <ForgotPasswordPage />
                  </Suspense>
                </PublicRoute>
              }
            />
            <Route
              path="reset-password/:token"
              element={
                <PublicRoute>
                  <Suspense
                    fallback={
                      <PageLoader message="Loading password reset..." />
                    }
                  >
                    <ResetPasswordPage />
                  </Suspense>
                </PublicRoute>
              }
            />
            <Route
              path="verify-email/:token"
              element={
                // <PublicRoute>
                  <Suspense
                    fallback={
                      <PageLoader message="Loading email verification..." />
                    }
                  >
                    <VerifyEmailPage />
                  </Suspense>
                // </PublicRoute>
              }
            />
            <Route
              path="verify-phone/:token"
              element={
                // <PublicRoute>
                  <Suspense
                    fallback={
                      <PageLoader message="Loading phone verification..." />
                    }
                  >
                    <VerifyPhonePage />
                  </Suspense>
                // </PublicRoute>
              }
            />
            <Route
              path="verify-email-pending"
              element={
                <PublicRoute>
                  <Suspense
                    fallback={
                      <PageLoader message="Loading verification page..." />
                    }
                  >
                    <VerifyEmailPendingPage />
                  </Suspense>
                </PublicRoute>
              }
            />
          </Route>

          {/* Invitation Route - Public */}
          <Route
            path="/invitation/:token"
            element={
              <Suspense
                fallback={
                  <PageLoader message="Loading invitation..." fullScreen />
                }
              >
                <InvitationAcceptancePage />
              </Suspense>
            }
          />

          {/* Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute
                allowedRoles={["super_admin", "admin"]}
                allowedCompanyRoles={[
                  "owner",
                  "manager",
                  "accountant",
                  "designer",
                  "maintenance_coordinator",
                  "field_staff",
                  "sales_agent",
                  "customer_support",
                ]}
              >
                <PermissionProvider>
                  <DashboardLayout />
                </PermissionProvider>
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Suspense
                  fallback={<PageLoader message="Loading dashboard..." />}
                >
                  <DashboardHomePage />
                </Suspense>
              }
            />
            <Route
              path="profile"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading profile..." />}
                >
                  <ProfilePage />
                </Suspense>
              }
            />
            <Route
              path="delete-account"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading..." />}
                >
                  <AdminDeleteAccount />
                </Suspense>
              }
            />
            <Route
              path="properties"
              element={
                <Suspense
                  fallback={
                    <PageLoader message="Loading property management..." />
                  }
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.PROPERTIES_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Properties.
                      </div>
                    }
                  >
                    <PropertyManagementPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="rental-units"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading rental units..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.RENTALUNIT_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Rental Units.
                      </div>
                    }
                  >
                    <PropertyManagementPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="rental-units/:id"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading rental unit..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.RENTALUNIT_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Rental Units.
                      </div>
                    }
                  >
                    <PropertyManagementPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="meter-readings"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading meter readings..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.RENTALUNIT_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Meter Readings.
                      </div>
                    }
                  >
                    <PropertyManagementPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="meter-readings/:propertyId"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading meter readings..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.RENTALUNIT_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Meter Readings.
                      </div>
                    }
                  >
                    <PropertyManagementPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="tenants"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading tenants..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.TENANTS_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Tenants.
                      </div>
                    }
                  >
                    <TenantsPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="companies"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading companies..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.COMPANY_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Companies.
                      </div>
                    }
                  >
                    <CompanyManagementPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="company-users"
              element={
                <Suspense fallback={<PageLoader message="Loading users..." />}>
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.USERS_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Company Users.
                      </div>
                    }
                  >
                    <CompanyUsersPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="company-users/:userId"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading user details..." />}
                >
                  <CompanyUsersPage />
                </Suspense>
              }
            />
            <Route
              path="bookings"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading bookings..." />}
                >
                    <BookingsPage />
                </Suspense>
              }
            />
            <Route
              path="bookings/list"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading bookings..." />}
                >
                    <BookingsPage />
                </Suspense>
              }
            />
            <Route
              path="bookings/configuration"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading configuration..." />}
                >
                    <BookingsPage />
                </Suspense>
              }
            />
            <Route
              path="maintenance"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading maintenance..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.MAINTENANCE_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Maintenance.
                      </div>
                    }
                  >
                    <MaintenancePage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="maintenance/:tab"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading maintenance..." />}
                >
                  <MaintenancePage />
                </Suspense>
              }
            />
            {/* Invoices Management Routes */}
            <Route
              path="invoices"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading invoices..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.INVOICES_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Invoices.
                      </div>
                    }
                  >
                    <InvoicesPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="invoices/view"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading invoices..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.INVOICES_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Invoices.
                      </div>
                    }
                  >
                    <InvoicesPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="invoices/create"
              element={
                <Suspense
                  fallback={
                    <PageLoader message="Loading invoice creation..." />
                  }
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.INVOICES_WRITE]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have permission to create invoices.
                      </div>
                    }
                  >
                    <InvoicesPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="invoices/templates"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading templates..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.INVOICES_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Invoice Templates.
                      </div>
                    }
                  >
                    <InvoicesPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            {/* Broadcasts Routes */}
            <Route
              path="broadcasts"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading broadcasts..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.BROADCAST_WRITE]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Broadcasts.
                      </div>
                    }
                  >
                    <BroadcastPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="broadcasts/*"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading broadcasts..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.BROADCAST_WRITE]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Broadcasts.
                      </div>
                    }
                  >
                    <BroadcastPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            {/* Payments Management Routes */}
            <Route
              path="payments"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading payments..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.PAYMENTS_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Payments.
                      </div>
                    }
                  >
                    <PaymentsPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="payments/payment-tracking"
              element={
                <Suspense
                  fallback={
                    <PageLoader message="Loading payment tracking..." />
                  }
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.PAYMENTS_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Payment Tracking.
                      </div>
                    }
                  >
                    <PaymentsPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="payments/payment-gateway"
              element={
                <Suspense
                  fallback={
                    <PageLoader message="Loading payment gateway..." />
                  }
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.PAYMENTS_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Payment Gateway.
                      </div>
                    }
                  >
                    <PaymentsPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            {/* Expenses Management Routes */}
            <Route
              path="expenses"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading expenses..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.EXPENSES_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Expenses.
                      </div>
                    }
                  >
                    <ExpensesPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="expenses/list"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading expenses..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.EXPENSES_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Expenses.
                      </div>
                    }
                  >
                    <ExpensesPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="expenses/accounts"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading payee accounts..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.EXPENSES_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Payee Accounts.
                      </div>
                    }
                  >
                    <ExpensesPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="expenses/payouts"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading payouts..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.EXPENSES_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Payouts.
                      </div>
                    }
                  >
                    <ExpensesPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="analytics"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading analytics..." />}
                >
                  {/* Gate Analytics behind feature permission on client as well */}
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.ANALYTICS_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Analytics.
                      </div>
                    }
                  >
                    <AnalyticsPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            {/* <Route path="chat" element={<ChatPage />} /> */}
            <Route
              path="notifications"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading notifications..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.NOTIFICATIONS_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Notifications.
                      </div>
                    }
                  >
                    <NotificationsPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="virtual-tours"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading virtual tours..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.ADMIN_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Virtual Tours.
                      </div>
                    }
                  >
                    <VirtualToursPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="map-demo"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading map demo..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.ADMIN_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Map Demo.
                      </div>
                    }
                  >
                    <MapDemoPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="admin"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading admin panel..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.ADMIN_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Admin Management.
                      </div>
                    }
                  >
                    <AdminManagementPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="plans"
              element={
                <SuperAdminRoute>
                  <Suspense
                    fallback={
                      <PageLoader message="Loading plans management..." />
                    }
                  >
                    <FeatureGate
                      features={[FEATURE_PERMISSIONS.ADMIN_READ]}
                      fallback={
                        <div className="p-6 text-gray-700">
                          You do not have access to Plans Management.
                        </div>
                      }
                    >
                      <PlansPage />
                    </FeatureGate>
                  </Suspense>
                </SuperAdminRoute>
              }
            />
            <Route
              path="bulk-onboarding"
              element={
                <SuperAdminRoute>
                  <Suspense
                    fallback={
                      <PageLoader message="Loading bulk onboarding..." />
                    }
                  >
                    <FeatureGate
                      features={[FEATURE_PERMISSIONS.ADMIN_READ]}
                      fallback={
                        <div className="p-6 text-gray-700">
                          You do not have access to Bulk Onboarding.
                        </div>
                      }
                    >
                      <BulkOnboardingPage />
                    </FeatureGate>
                  </Suspense>
                </SuperAdminRoute>
              }
            />
            <Route
              path="test-notifications"
              element={
                <Suspense
                  fallback={
                    <PageLoader message="Loading test notifications..." />
                  }
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.ADMIN_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Test Notifications.
                      </div>
                    }
                  >
                    <TestNotificationsPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="notification-logs"
              element={
                <Suspense
                  fallback={
                    <PageLoader message="Loading notification logs..." />
                  }
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.ADMIN_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Notification Logs.
                      </div>
                    }
                  >
                    <NotificationLogsPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="auth-logs"
              element={
                <Suspense fallback={<PageLoader message="Loading auth logs..." />}>
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.ADMIN_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Auth Logs.
                      </div>
                    }
                  >
                    <AuthLogsPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="chargebee-logs"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading Chargebee logs..." />}
                >
                  <FeatureGate
                    features={[FEATURE_PERMISSIONS.ADMIN_READ]}
                    fallback={
                      <div className="p-6 text-gray-700">
                        You do not have access to Chargebee Logs.
                      </div>
                    }
                  >
                    <ChargebeeLogsPage />
                  </FeatureGate>
                </Suspense>
              }
            />
            <Route
              path="leads"
              element={
                <SuperAdminRoute>
                  <Suspense
                    fallback={<PageLoader message="Loading leads..." />}
                  >
                    <FeatureGate
                      features={[FEATURE_PERMISSIONS.ADMIN_READ]}
                      fallback={
                        <div className="p-6 text-gray-700">
                          You do not have access to Leads.
                        </div>
                      }
                    >
                      <LeadsPage />
                    </FeatureGate>
                  </Suspense>
                </SuperAdminRoute>
              }
            />
            <Route
              path="catalogue"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading catalogue config..." />}
                >
                  <FeatureGate
                      features={[FEATURE_PERMISSIONS.COMPANY_CATALOGUE]}
                      fallback={
                        <div className="p-6 text-gray-700">
                          You do not have access to Catalogue Config.
                        </div>
                      }
                    >
                    <CatalogueConfigPage />
                  </FeatureGate>
                </Suspense>
              }
            />
          </Route>

          {/* Tenant Routes */}
          <Route
            path="/tenant"
            element={
              <ProtectedRoute allowedRoles={["tenant"]}>
                <TenantLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Suspense
                  fallback={
                    <PageLoader message="Loading tenant dashboard..." />
                  }
                >
                  <TenantDashboard />
                </Suspense>
              }
            />
            <Route
              path="units"
              element={
                <Suspense fallback={<PageLoader message="Loading units..." />}>
                  <TenantUnitsList />
                </Suspense>
              }
            />
            <Route
              path="units/:id"
              element={
                <Suspense fallback={<PageLoader message="Loading unit..." />}>
                  <TenantUnitDetail />
                </Suspense>
              }
            />
            <Route
              path="properties"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading properties..." />}
                >
                  <PropertiesPage />
                </Suspense>
              }
            />
            <Route
              path="properties/:id"
              element={
                <Suspense
                  fallback={
                    <PageLoader message="Loading property details..." />
                  }
                >
                  <SinglePropertyPage />
                </Suspense>
              }
            />
            <Route
              path="invoices"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading invoices..." />}
                >
                  <TenantInvoicesPage />
                </Suspense>
              }
            />
            <Route
              path="delete-account"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading..." />}
                >
                  <TenantDeleteAccount />
                </Suspense>
              }
            />
            <Route
              path="maintenance"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading maintenance..." />}
                >
                  <TenantMaintenancePage />
                </Suspense>
              }
            />
            <Route
              path="notifications"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading notifications..." />}
                >
                  <TenantNotificationsPage />
                </Suspense>
              }
            />
            <Route path="messages" element={<div>Messages Page</div>} />
            {/* <Route path="chat" element={<div>Chat Page - Using TenantChat component in layout</div>} /> */}
            <Route
              path="profile"
              element={
                <Suspense
                  fallback={<PageLoader message="Loading profile..." />}
                >
                  <TenantProfileView />
                </Suspense>
              }
            />
            <Route path="settings" element={<div>Settings Page</div>} />
          </Route>

          {/* Role-based Dashboard Redirect */}
          <Route path="/app" element={<RoleBasedRedirect />} />

          {/* Redirects */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Navigate to="/auth/login" replace />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Navigate to="/auth/register" replace />
              </PublicRoute>
            }
          />

          {/* 404 Page */}
          <Route
            path="*"
            element={
              <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-purple-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 px-4">
                <div className="text-center max-w-lg">
                  {/* Logo */}
                  <div className="mb-8 flex justify-center">
                    <img 
                      src="/assets/rentBhaiLogo.svg" 
                      alt={BRAND_NAME} 
                      className="h-16 w-auto"
                    />
                  </div>
                  
                  {/* 404 Animation */}
                  <div className="relative mb-8">
                    <h1 className="text-[150px] font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 leading-none select-none animate-pulse">
                      404
                    </h1>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-32 h-32 border-4 border-purple-300 dark:border-purple-700 rounded-full animate-ping opacity-20"></div>
                    </div>
                  </div>
                  
                  {/* Message */}
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    Oops! Page Not Found
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-10 text-lg">
                    The page you're looking for doesn't exist or has been moved.
                  </p>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link 
                      to="/auth/register?form=tenant" 
                      className="inline-flex items-center justify-center px-8 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-105 transition-all duration-300"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                      Register Now
                    </Link>
                    <a 
                      href="https://rentbhai.com" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-8 py-3.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-600 hover:scale-105 transition-all duration-300 shadow-sm"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      Explore {BRAND_NAME}
                    </a>
                  </div>
                  
                  {/* Login Link */}
                  <p className="mt-8 text-gray-500 dark:text-gray-400">
                    Already have an account?{" "}
                    <Link to="/auth/login" className="text-purple-600 dark:text-purple-400 font-medium hover:underline">
                      Sign In
                    </Link>
                  </p>
                </div>
              </div>
            }
          />
        </Routes>

        {/* Global Components */}
        <Toast />
        <RBChatbot />
      </div>
    </Router>
  );
}

function App() {
  const gtmId = process.env.REACT_APP_GTM_ID || "";
  const gtmEnabled = Boolean(gtmId);

  return (
    <Provider store={store}>
      <HelmetProvider>
        <GTMScript gtmId={gtmId} enabled={gtmEnabled} />
        <AppContent />
      </HelmetProvider>
    </Provider>
  );
}

export default App;
